import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { sanitizeInput, formatCuit, formatDateDDMMYYYY } from '../../utils/inputValidation';
import { getScoringListing } from '../../services/userservice';
import HondaLoader from '../../components/common/HondaLoader';
import Swal from 'sweetalert2';
const AnalisisCrediticioDisponible = ({ user, onLogout, onNavigate }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    // Función para calcular los días desde la fecha de aceptación hasta hoy
    const calcularAging = (fechaAceptacion) => {
        if (!fechaAceptacion || fechaAceptacion === 'N/A') {
            return 0;
        }
        try {
            let fechaAcept;
            // Parsear la fecha según el formato
            if (fechaAceptacion.includes('/')) {
                // Formato día/mes/año (dd/mm/yyyy)
                const partes = fechaAceptacion.split('/');
                if (partes.length === 3) {
                    const dia = parseInt(partes[0], 10);
                    const mes = parseInt(partes[1], 10) - 1; // Meses en JS van de 0-11
                    const anio = parseInt(partes[2], 10);
                    fechaAcept = new Date(anio, mes, dia);
                } else {
                    fechaAcept = new Date(fechaAceptacion);
                }
            } else if (fechaAceptacion.includes('-')) {
                // Puede ser formato ISO (yyyy-mm-dd) o latino (dd-mm-yyyy)
                const partes = fechaAceptacion.split('-');
                if (partes.length === 3) {
                    if (partes[0].length === 4) {
                        // Formato ISO año-mes-día (yyyy-mm-dd)
                        fechaAcept = new Date(fechaAceptacion + 'T00:00:00');
                    } else {
                        // Formato día-mes-año (dd-mm-yyyy)
                        const dia = parseInt(partes[0], 10);
                        const mes = parseInt(partes[1], 10) - 1;
                        const anio = parseInt(partes[2], 10);
                        fechaAcept = new Date(anio, mes, dia);
                    }
                } else {
                    fechaAcept = new Date(fechaAceptacion);
                }
            } else {
                // Intentar parsearlo directamente
                fechaAcept = new Date(fechaAceptacion);
            }
            const hoy = new Date();
            // Resetear las horas para comparar solo días
            fechaAcept.setHours(0, 0, 0, 0);
            hoy.setHours(0, 0, 0, 0);
            // Calcular diferencia en milisegundos y convertir a días
            const diferenciaMilisegundos = hoy - fechaAcept;
            const dias = Math.floor(diferenciaMilisegundos / (1000 * 60 * 60 * 24));
            return dias >= 0 ? dias : 0;
        } catch (error) {
            console.error('Error calculando aging para fecha:', fechaAceptacion, error);
            return 0;
        }
    };
    // Cargar datos al montar el componente
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                // Obtener scoring disponibles (puedes cambiar el status según necesites)
                // Opciones posibles: 'DISPONIBLES', 'INCOMPLETOS', 'RECHAZADOS'
                const response = await getScoringListing('DISPONIBLES', user.ce_ID);
                if (!response || !Array.isArray(response)) {
                    throw new Error('La respuesta del servidor no es válida');
                }
                // Mapear la respuesta al formato esperado por la tabla
                const mappedData = response.map((item, index) => {
                    // Extraer concesionario de manera segura
                    let concesionario = 'N/A';
                    if (item.sellerHouse && typeof item.sellerHouse === 'object') {
                        concesionario = item.sellerHouse.ceName || 'N/A';
                    } else if (item.concesionario && typeof item.concesionario === 'string') {
                        concesionario = item.concesionario;
                    }
                    // Extraer fecha de aceptación
                    const fechaAceptacion = item.acceptDate || item.aceptacion || 'N/A';
                    // Formatear fecha a dd/mm/yyyy
                    const fechaFormateada = formatDateDDMMYYYY(fechaAceptacion);
                    // Calcular aging desde la fecha de aceptación hasta hoy
                    const agingCalculado = calcularAging(fechaAceptacion);
                    return {
                        id: item.id || index + 1,
                        grupo: item.group || item.grupo,
                        orden: item.order || item.orden,
                        titular: item.mainName || item.titularName || item.titular || 'N/A',
                        cuit: item.cuil || item.cuit || 'N/A',
                        aceptacion: fechaFormateada,
                        deuda: item.debtPercent ? `${item.debtPercent}%` : item.deuda || '0%',
                        concesionario: concesionario,
                        estado: item.status || item.estado || 'DISPONIBLES',
                        aging: agingCalculado
                    };
                });
                setData(mappedData);
                if (mappedData.length === 0) {
                    console.warn('No se encontraron casos disponibles');
                    Swal.fire({
                        icon: 'info',
                        title: 'Sin casos disponibles',
                        text: 'No hay casos de scoring disponibles en este momento.',
                        confirmButtonColor: '#ef4444',
                    });
                }
            } catch (err) {
                console.error('Error cargando scoring disponibles:', err);
                Swal.fire({
                    icon: 'error',
                    title: 'Error al cargar datos',
                    text: `No se pudieron cargar los casos de scoring disponibles: ${err.message}`,
                    confirmButtonColor: '#ef4444',
                });
                // Limpiar datos en caso de error
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);
    // Filtrar datos según el término de búsqueda
    const filteredData = data.filter(row => {
        const searchLower = searchTerm.toLowerCase();
        const grupo = String(row.grupo || '').toLowerCase();
        const orden = String(row.orden || '').toLowerCase();
        const titular = String(row.titular || '').toLowerCase();
        const cuit = String(row.cuit || '').toLowerCase();
        const concesionario = String(row.concesionario || '').toLowerCase();
        return grupo.includes(searchLower) ||
            orden.includes(searchLower) ||
            titular.includes(searchLower) ||
            cuit.includes(searchLower) ||
            concesionario.includes(searchLower);
    });
    const handleRowClick = (row) => {
        // Guardar datos completos en localStorage para que AnalisisCrediticio los pueda leer
        const scoringData = {
            grupo: row.grupo,
            orden: row.orden,
            estado: 'DISPONIBLES',
            // Datos adicionales del titular/caso
            titular: row.titular,
            cuil: row.cuit,
            aceptacion: row.aceptacion,
            porcentajeDeuda: row.deuda,
            concesionario: row.concesionario,
            aging: row.aging,
            // Flag para indicar que viene de la lista de disponibles
            fromAvailableList: true
        };
        localStorage.setItem('scoringData', JSON.stringify(scoringData));
        // Navegar a la vista AnalisisCrediticio
        onNavigate('adminAnalisisCrediticio');
    };
    const getEstadoColor = (estado) => {
        switch (estado) {
            case 'APROBADO':
                return 'text-green-600 bg-green-50';
            case 'RECHAZADO':
                return 'text-red-600 bg-red-50';
            case 'PENDIENTE':
                return 'text-yellow-600 bg-yellow-50';
            case 'EN REVISIÓN':
                return 'text-blue-600 bg-blue-50';
            default:
                return 'text-gray-600 bg-gray-50';
        }
    };
    return (
        <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
            <div className="max-w-full mx-auto px-3 py-4">
                {/* Título y botón en la misma línea */}
                <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
                            <h2 className="text-2xl font-bold text-gradient">
                                Análisis Crediticio Disponibles
                            </h2>
                        </div>
                        <p className="text-gray-500 text-sm ml-5">Administre los casos pendientes de análisis crediticio</p>
                    </div>
                    {/* <button
                        onClick={handleExportExcel}
                        className="text-green-600 hover:bg-green-600 hover:text-white transition-colors p-2 rounded-lg border-2 border-green-600 inline-flex items-center justify-center gap-2"
                        title="Exportar a Excel"
                    >
                        <span className="material-icons text-xl">table_chart</span>
                       {/* <span className="text-sm font-medium hidden sm:inline">Exportar a Excel</span> */}
                    {/* </button> */}
                </div>
                {/* Buscador */}
                <div className="mb-4 w-full">
                    <div className="relative w-full max-w-2xl">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center">
                            <span className="material-icons text-gray-400 text-lg">
                                search
                            </span>
                        </span>
                        <input
                            type="text"
                            placeholder="Buscar por Grupo, Orden, Titular, DNI o Concesionario..."
                            value={searchTerm}
                            onChange={(e) => {
                                const sanitized = sanitizeInput(e.target.value);
                                setSearchTerm(sanitized);
                            }}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-gray-700 text-sm focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                        />
                    </div>
                </div>
                {/* Tabla */}
                {loading ? (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12">
                        <HondaLoader 
                            message="Cargando casos disponibles..." 
                            variant="inline"
                            size="large"
                            subtitle="Por favor espere mientras obtenemos la información"
                        />
                    </div>
                ) : (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gradient-to-r from-red-600 to-red-700">
                                <tr>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Grupo</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Orden</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Titular</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">DNI / CUIT</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Aceptación</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">% de Deuda</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Concesionario</th>
                                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Estado</th>
                                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Aging</th>
                                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredData.length > 0 ? (
                                    filteredData.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="hover:bg-gray-50 transition-colors"
                                        >
                                            <td className="px-4 py-3 text-sm text-gray-900">{row.grupo}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{row.orden}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{row.titular}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{formatCuit(row.cuit)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">{row.aceptacion}</td>
                                            <td className="px-4 py-3 text-sm">
                                                <span className={`inline-flex px-2 py-1 rounded-full text-xs ${
                                                    parseFloat(row.deuda.replace(',', '.')) > 66 ? 'bg-red-100 text-red-700' :
                                                    parseFloat(row.deuda.replace(',', '.')) > 33 ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-green-100 text-green-700'
                                                }`}>
                                                    {row.deuda}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={row.concesionario}>{row.concesionario}</td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                {row.estado}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-center text-gray-900">
                                                {row.aging}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRowClick(row);
                                                    }}
                                                    className="text-green-600 hover:bg-green-600 hover:text-white transition-colors p-1 rounded-full inline-flex items-center justify-center"
                                                    title="Ver detalle"
                                                >
                                                    <span className="material-icons text-xl">arrow_forward</span>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                                            No se encontraron resultados
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                )}
            </div>
        </MainLayout>
    );
};
export default AnalisisCrediticioDisponible;

